import { onValue, push, ref, remove } from 'firebase/database';
import { useEffect, useRef, useState } from 'react';
import { database } from '../firebase';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const PRESET_FESTIVALS = [
  { date: '2025-01-14', name: 'Makar Sankranti',   type: 'festival' },
  { date: '2025-01-26', name: 'Republic Day',       type: 'national' },
  { date: '2025-03-14', name: 'Holi',               type: 'festival' },
  { date: '2025-03-31', name: 'Id-ul-Fitr (Eid)',   type: 'festival' },
  { date: '2025-04-14', name: 'Dr. Ambedkar Jayanti', type: 'national' },
  { date: '2025-04-18', name: 'Good Friday',        type: 'festival' },
  { date: '2025-05-12', name: 'Buddha Purnima',     type: 'festival' },
  { date: '2025-06-07', name: 'Id-ul-Zuha (Bakrid)', type: 'festival' },
  { date: '2025-08-15', name: 'Independence Day',   type: 'national' },
  { date: '2025-08-16', name: 'Janmashtami',        type: 'festival' },
  { date: '2025-08-27', name: 'Ganesh Chaturthi',   type: 'festival' },
  { date: '2025-10-02', name: 'Gandhi Jayanti',     type: 'national' },
  { date: '2025-10-02', name: 'Dussehra',           type: 'festival' },
  { date: '2025-10-20', name: 'Diwali',             type: 'festival' },
  { date: '2025-10-21', name: 'Govardhan Puja',     type: 'festival' },
  { date: '2025-10-22', name: 'Bhai Dooj',          type: 'festival' },
  { date: '2025-11-05', name: 'Guru Nanak Jayanti', type: 'festival' },
  { date: '2025-12-25', name: 'Christmas',          type: 'festival' },
];

const TYPE_COLORS = {
  holiday:  { bg: '#fff3cd', border: '#ffc107', text: '#856404', badge: '#ffc107' },
  festival: { bg: '#fde8f0', border: '#e91e8c', text: '#9c1060', badge: '#e91e8c' },
  national: { bg: '#d4edda', border: '#28a745', text: '#155724', badge: '#28a745' },
  event:    { bg: '#d1ecf1', border: '#17a2b8', text: '#0c5460', badge: '#17a2b8' },
};

const CalendarPage = () => {
  const today      = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState({});       // { 'YYYY-MM-DD': [{id, name, type}] }
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [form, setForm] = useState({ name: '', type: 'holiday' });
  const [saving, setSaving] = useState(false);
  const initialized = useRef(false);

  // Load from Firebase
  useEffect(() => {
    const unsub = onValue(ref(database, 'calendar_events'), (snap) => {
      const raw = snap.val() || {};
      const grouped = {};
      Object.entries(raw).forEach(([id, v]) => {
        if (!grouped[v.date]) grouped[v.date] = [];
        grouped[v.date].push({ id, ...v });
      });
      setEvents(grouped);
    });
    return () => unsub();
  }, []);

  // Seed preset festivals once
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    onValue(ref(database, 'calendar_events'), (snap) => {
      if (!snap.exists()) {
        PRESET_FESTIVALS.forEach(f => push(ref(database, 'calendar_events'), f));
      }
    }, { onlyOnce: true });
  }, []);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const goToday   = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const firstDay  = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev  = new Date(year, month, 0).getDate();

  const pad = (n) => String(n).padStart(2, '0');
  const dateKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

  // Build calendar grid
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push({ day: daysInPrev - firstDay + 1 + i, cur: false });
  for (let i = 1; i <= daysInMonth; i++) cells.push({ day: i, cur: true });
  const remaining = 42 - cells.length;
  for (let i = 1; i <= remaining; i++) cells.push({ day: i, cur: false });

  const handleDayClick = (cell) => {
    if (!cell.cur) return;
    const key = dateKey(year, month, cell.day);
    setSelectedDate(key);
    setForm({ name: '', type: 'holiday' });
    setShowModal(true);
  };

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await push(ref(database, 'calendar_events'), { date: selectedDate, name: form.name.trim(), type: form.type });
    setSaving(false);
    setShowModal(false);
  };

  const handleDelete = async (id) => {
    await remove(ref(database, `calendar_events/${id}`));
  };

  // Upcoming events (next 60 days)
  const upcoming = [];
  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());
  Object.entries(events).forEach(([date, evts]) => {
    if (date >= todayKey) evts.forEach(e => upcoming.push({ date, ...e }));
  });
  upcoming.sort((a, b) => a.date.localeCompare(b.date));

  const isToday = (cell) => {
    if (!cell.cur) return false;
    return year === today.getFullYear() && month === today.getMonth() && cell.day === today.getDate();
  };

  return (
    <>
      <style>{`
        .cal-wrap { font-size: 13px; }
        .cal-header {
          background: #4d3d6c;
          color: #fff;
          border-radius: 12px 12px 0 0;
          padding: 14px 20px;
          display: flex; align-items: center; justify-content: space-between;
        }
        .cal-header h5 { margin: 0; font-weight: 700; font-size: 16px; }
        .cal-nav-btn {
          background: rgba(255,255,255,0.15);
          border: none; color: #fff;
          border-radius: 6px; padding: 4px 12px;
          cursor: pointer; font-size: 15px;
          transition: background 0.2s;
        }
        .cal-nav-btn:hover { background: rgba(255,255,255,0.3); }
        .cal-today-btn {
          background: #90b732; border: none; color: #fff;
          border-radius: 6px; padding: 4px 14px;
          cursor: pointer; font-size: 12px; font-weight: 600;
          transition: background 0.2s;
        }
        .cal-today-btn:hover { background: #6fa020; }

        .cal-grid { border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 12px 12px; overflow: hidden; }
        .cal-days-header {
          display: grid; grid-template-columns: repeat(7, 1fr);
          background: #f8fdf0;
        }
        .cal-day-name {
          text-align: center; padding: 8px 0;
          font-weight: 700; font-size: 12px; color: #4d3d6c;
          border-right: 1px solid #eee;
        }
        .cal-day-name:last-child { border-right: none; }
        .cal-cells { display: grid; grid-template-columns: repeat(7, 1fr); }
        .cal-cell {
          min-height: 80px; padding: 6px;
          border-right: 1px solid #eee; border-bottom: 1px solid #eee;
          cursor: pointer; transition: background 0.15s;
          position: relative;
        }
        .cal-cell:nth-child(7n) { border-right: none; }
        .cal-cell:hover { background: #f8fdf0; }
        .cal-cell.other-month { background: #fafafa; cursor: default; }
        .cal-cell.other-month:hover { background: #fafafa; }
        .cal-cell.is-today { background: #ecf3d7; }
        .cal-cell.is-today .cal-date-num { background: #90b732; color: #fff; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; }
        .cal-cell.is-sunday .cal-date-num { color: #e74c3c; }
        .cal-date-num { font-size: 13px; font-weight: 600; color: #333; margin-bottom: 3px; display: inline-block; }
        .cal-event-pill {
          display: flex; align-items: center; justify-content: space-between;
          border-radius: 4px; padding: 2px 5px; margin-bottom: 2px;
          font-size: 10px; font-weight: 600; line-height: 1.3;
          cursor: default;
        }
        .cal-event-pill .del-btn {
          background: none; border: none; padding: 0 0 0 4px;
          cursor: pointer; opacity: 0.6; font-size: 11px; line-height: 1;
          transition: opacity 0.2s;
        }
        .cal-event-pill .del-btn:hover { opacity: 1; }
        .cal-add-hint {
          position: absolute; bottom: 4px; right: 5px;
          font-size: 16px; color: #ccc; line-height: 1;
          transition: color 0.2s;
        }
        .cal-cell:hover .cal-add-hint { color: #90b732; }
        .cal-cell.other-month .cal-add-hint { display: none; }

        /* Upcoming panel */
        .upcoming-panel { border: 1px solid #e0e0e0; border-radius: 12px; overflow: hidden; }
        .upcoming-header { background: #4d3d6c; color: #fff; padding: 12px 16px; font-weight: 700; font-size: 14px; }
        .upcoming-item {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 16px; border-bottom: 1px solid #f0f0f0;
          transition: background 0.15s;
        }
        .upcoming-item:last-child { border-bottom: none; }
        .upcoming-item:hover { background: #f8fdf0; }
        .upcoming-date-box {
          min-width: 44px; text-align: center;
          background: #ecf3d7; border-radius: 8px; padding: 4px 6px;
        }
        .upcoming-date-box .ud { font-size: 18px; font-weight: 800; color: #4d3d6c; line-height: 1; }
        .upcoming-date-box .um { font-size: 10px; color: #90b732; font-weight: 700; text-transform: uppercase; }
        .upcoming-name { font-weight: 600; color: #2d2d2d; font-size: 13px; }
        .upcoming-type { font-size: 10px; font-weight: 700; border-radius: 50px; padding: 2px 8px; color: #fff; }

        /* Legend */
        .cal-legend { display: flex; gap: 14px; flex-wrap: wrap; margin-top: 10px; }
        .cal-legend-item { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #555; }
        .cal-legend-dot { width: 10px; height: 10px; border-radius: 3px; }

        /* Modal */
        .cal-modal-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.45); z-index: 1050;
          display: flex; align-items: center; justify-content: center;
        }
        .cal-modal {
          background: #fff; border-radius: 14px;
          width: 100%; max-width: 420px;
          box-shadow: 0 16px 48px rgba(0,0,0,0.18);
          overflow: hidden;
          animation: calModalIn 0.25s ease;
        }
        @keyframes calModalIn { from { opacity:0; transform:scale(0.94); } to { opacity:1; transform:scale(1); } }
        .cal-modal-header { background: #4d3d6c; color: #fff; padding: 14px 18px; display: flex; align-items: center; justify-content: space-between; }
        .cal-modal-header h6 { margin: 0; font-weight: 700; }
        .cal-modal-body { padding: 20px 18px; }
        .cal-modal-footer { padding: 12px 18px; border-top: 1px solid #eee; display: flex; justify-content: flex-end; gap: 8px; }
      `}</style>

      <div className="cal-wrap">
        <div className="d-flex align-items-center justify-content-between mb-3 pb-2" style={{ borderBottom: '2px solid #4d3d6c' }}>
          <h6 className="fw-bold mb-0" style={{ color: '#4d3d6c' }}>
            <i className="bi bi-calendar3 me-2"></i>Academic Calendar
          </h6>
          <button className="btn btn-sm btn-outline-primary" onClick={() => { setSelectedDate(todayKey); setForm({ name: '', type: 'holiday' }); setShowModal(true); }}>
            <i className="bi bi-plus-lg me-1"></i>Add Event
          </button>
        </div>

        <div className="row g-3">
          {/* Calendar */}
          <div className="col-lg-8">
            <div className="cal-header">
              <div className="d-flex gap-2">
                <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
                <button className="cal-nav-btn" onClick={nextMonth}>›</button>
              </div>
              <h5>{MONTHS[month]} {year}</h5>
              <button className="cal-today-btn" onClick={goToday}>Today</button>
            </div>

            <div className="cal-grid">
              <div className="cal-days-header">
                {DAYS.map(d => <div key={d} className="cal-day-name">{d}</div>)}
              </div>
              <div className="cal-cells">
                {cells.map((cell, i) => {
                  const key = cell.cur ? dateKey(year, month, cell.day) : null;
                  const dayEvents = key ? (events[key] || []) : [];
                  const isSun = i % 7 === 0;
                  return (
                    <div
                      key={i}
                      className={`cal-cell ${!cell.cur ? 'other-month' : ''} ${isToday(cell) ? 'is-today' : ''} ${isSun && cell.cur ? 'is-sunday' : ''}`}
                      onClick={() => handleDayClick(cell)}
                    >
                      <span className="cal-date-num" style={isSun && cell.cur && !isToday(cell) ? { color: '#e74c3c' } : {}}>
                        {cell.day}
                      </span>
                      {dayEvents.map(ev => {
                        const c = TYPE_COLORS[ev.type] || TYPE_COLORS.event;
                        return (
                          <div key={ev.id} className="cal-event-pill" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>{ev.name}</span>
                            <button className="del-btn" onClick={e => { e.stopPropagation(); handleDelete(ev.id); }}>✕</button>
                          </div>
                        );
                      })}
                      {cell.cur && <span className="cal-add-hint">+</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="cal-legend mt-2">
              {Object.entries(TYPE_COLORS).map(([type, c]) => (
                <div key={type} className="cal-legend-item">
                  <div className="cal-legend-dot" style={{ background: c.badge }}></div>
                  <span className="text-capitalize">{type}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="col-lg-4">
            <div className="upcoming-panel">
              <div className="upcoming-header">
                <i className="bi bi-calendar-event me-2"></i>Upcoming Events
              </div>
              <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                {upcoming.length === 0 ? (
                  <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>No upcoming events</div>
                ) : upcoming.slice(0, 20).map((ev, i) => {
                  const d = new Date(ev.date + 'T00:00:00');
                  const c = TYPE_COLORS[ev.type] || TYPE_COLORS.event;
                  return (
                    <div key={i} className="upcoming-item">
                      <div className="upcoming-date-box">
                        <div className="ud">{d.getDate()}</div>
                        <div className="um">{MONTHS[d.getMonth()].slice(0,3)}</div>
                      </div>
                      <div className="flex-grow-1">
                        <div className="upcoming-name">{ev.name}</div>
                        <span className="upcoming-type mt-1 d-inline-block" style={{ background: c.badge }}>{ev.type}</span>
                      </div>
                      <button className="btn btn-sm btn-outline-danger py-0 px-1" style={{ fontSize: 11 }} onClick={() => handleDelete(ev.id)}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Event Modal */}
      {showModal && (
        <div className="cal-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="cal-modal" onClick={e => e.stopPropagation()}>
            <div className="cal-modal-header">
              <h6><i className="bi bi-calendar-plus me-2"></i>Add Event — {selectedDate}</h6>
              <button className="btn-close btn-close-white btn-sm" onClick={() => setShowModal(false)}></button>
            </div>
            <div className="cal-modal-body">
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Event / Holiday Name <span className="text-danger">*</span></label>
                <input
                  className="form-control form-control-sm"
                  placeholder="e.g. Diwali, Institute Holiday..."
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Type</label>
                <div className="d-flex gap-2 flex-wrap">
                  {Object.entries(TYPE_COLORS).map(([type, c]) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, type }))}
                      style={{
                        background: form.type === type ? c.badge : '#f5f5f5',
                        color: form.type === type ? '#fff' : '#555',
                        border: `1.5px solid ${form.type === type ? c.badge : '#ddd'}`,
                        borderRadius: 6, padding: '5px 14px',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        transition: 'all 0.15s',
                        textTransform: 'capitalize',
                      }}
                    >{type}</button>
                  ))}
                </div>
              </div>
              <div className="mb-1">
                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                />
              </div>
            </div>
            <div className="cal-modal-footer">
              <button className="btn btn-sm btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-sm btn-primary" onClick={handleAdd} disabled={saving || !form.name.trim()}>
                {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : <><i className="bi bi-check-lg me-1"></i>Add Event</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarPage;
